import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';

import { Service } from 'app/models/services';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-service',
  templateUrl: './modifier-service.component.html',
  styleUrls: ['./modifier-service.component.scss']
})
export class ModifierServiceComponent implements OnInit {
  // Service data
  service: Service = new Service();
  agenciesList: any[] = [];
  idService: string | null = null;
  selectedAgencyId: string | null = null; // For navigation

  // Main image only
  selectedFiles: FileList | null = null;
  isUploading: boolean = false;
  uploadedMainImage: string | null = null;

  // Form state
  isSubmitting: boolean = false;

  // Category management
  categoryInput: string = '';

  // Video URL
  videoUrl: string = '';

  // ✅ hasAtelier toggle
  hasAtelier: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private agenciesService: AgenciesService,
    private servicesService: ServicesService,
    private awsService: AwscontrolService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
    this.loadAgencies();

    this.servicesService.refreshNedeed.subscribe(() => {
      this.loadServiceDetails();
    });

    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => {
        console.log('✅ AWS S3 Connection Healthy:', response);
      },
      error: (err) => {
        console.warn('⚠️ AWS S3 Connection Warning:', err);
      }
    });
  }

  loadServiceDetails(): void {
    if (this.idService) {
      this.servicesService.getService(this.idService).subscribe({
        next: (data: Service) => {
          this.service = data;
          this.uploadedMainImage = data.imageUrl || null;
          this.videoUrl = data.videoUrl || '';
          // ✅ Load hasAtelier from service data
          this.hasAtelier = data.hasAtelier || false;

          // Find agency ID from service.agencyName
          if (this.service.agencyName && this.agenciesList.length > 0) {
            const found = this.agenciesList.find(a => a.name === this.service.agencyName);
            if (found) {
              this.selectedAgencyId = found.id;
            }
          }
        },
        error: (error) => {
          console.error('Error loading service:', error);
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: 'Impossible de charger les détails du service',
            confirmButtonColor: '#f44336'
          });
        }
      });
    }
  }

  loadAgencies(): void {
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        this.agenciesList = data;
        // If service already loaded, find agency ID
        if (this.service && this.service.agencyName) {
          const found = this.agenciesList.find(a => a.name === this.service.agencyName);
          if (found) {
            this.selectedAgencyId = found.id;
          }
        }
      },
      error: (error) => {
        console.error('Error loading agencies:', error);
      }
    });
  }

  onAgencyChange(agencyName: string): void {
    // Update selectedAgencyId when agency changes
    const found = this.agenciesList.find(a => a.name === agencyName);
    if (found) {
      this.selectedAgencyId = found.id;
    }
  }

  // Format text methods
  formatText(command: string): void {
    if (!this.service.description) this.service.description = '';

    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.service.description.substring(start, end);

    let formattedText = '';
    switch (command) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
    }

    const newText = this.service.description.substring(0, start) + formattedText + this.service.description.substring(end);
    this.service.description = newText;

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  // Category management
  addCategory(): void {
    if (this.categoryInput.trim()) {
      if (!this.service.categoryList) {
        this.service.categoryList = [];
      }
      this.service.categoryList.push(this.categoryInput.trim());
      this.categoryInput = '';
    }
  }

  removeCategory(index: number): void {
    if (this.service.categoryList) {
      this.service.categoryList.splice(index, 1);
    }
  }

  // Image handling (main only)
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.selectedFiles = files;
      this.uploadMainImage();
    }
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: 'warning',
        title: 'Format non supporté',
        text: 'JPEG, PNG, JPG, GIF, WEBP uniquement',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier trop volumineux',
        text: 'Max 5MB',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    return true;
  }

  uploadMainImage(): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    const file = this.selectedFiles[0];
    if (!this.validateImageFile(file)) return;

    this.isUploading = true;
    const folder = 'service-images';

    this.awsService.uploadFile(file, folder).subscribe({
      next: (response) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          const url = response.urls[0];
          this.uploadedMainImage = url;
          this.service.imageUrl = url;
          Swal.fire({
            icon: 'success',
            title: 'Image mise à jour',
            text: 'L\'image principale a été téléchargée avec succès',
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: response.error || 'Échec du téléchargement',
            confirmButtonColor: '#f44336'
          });
        }
      },
      error: (err: any) => {
        this.isUploading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de télécharger l\'image',
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  removeMainImage(): void {
    Swal.fire({
      title: 'Supprimer l\'image?',
      text: 'Voulez-vous vraiment supprimer l\'image principale?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.uploadedMainImage = null;
        this.service.imageUrl = '';
      }
    });
  }

  // Form submission (with videoUrl and hasAtelier)
  updateService(): void {
    if (!this.service.name || this.service.name.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Nom requis',
        text: 'Veuillez saisir un nom pour le service',
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    this.isSubmitting = true;

    // ✅ Prepare service data – include hasAtelier
    const serviceData = {
      name: this.service.name || '',
      description: this.service.description || '',
      agencyName: this.service.agencyName || '',
      categoryList: this.service.categoryList || [],
      imageUrl: this.service.imageUrl || '',
      videoUrl: this.videoUrl || '',
      hasAtelier: this.hasAtelier   // ✅ added
      // removed: categorie, status, prix, promoTitle, promoImg
    };

    console.log('Service data to update:', serviceData);

    if (!this.idService) {
      Swal.fire({
        icon: 'error',
        title: 'ID manquant',
        text: 'Impossible de mettre à jour le service sans ID',
        confirmButtonColor: '#f44336'
      });
      this.isSubmitting = false;
      return;
    }

    this.servicesService.updateService(this.idService, serviceData).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès!',
          text: 'Service mis à jour avec succès',
          confirmButtonColor: '#4caf50',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          this.goToServiceList();
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.handleError(error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = 'Une erreur est survenue lors de la mise à jour du service';
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.error?.text) {
      errorMessage = error.error.text;
    }

    Swal.fire({
      icon: 'error',
      title: 'Erreur',
      text: errorMessage,
      confirmButtonColor: '#f44336'
    });
  }

  // Navigation
  private goToServiceList(): void {
    if (this.selectedAgencyId) {
      this.router.navigate(['/list-Services', this.selectedAgencyId]);
    } else {
      this.location.back();
    }
  }

  cancel(): void {
    this.goToServiceList();
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser les modifications?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadServiceDetails();
        this.categoryInput = '';
      }
    });
  }
}